/**
 * NOS-94: Floating Brand panel - theme presets, primary/secondary colors, font, logo.
 * NOS-153: Brand Panel v2 — token-based theme editor (Palette / Typography / Feel)
 * on top of the design token system (docs/specs/BUILDER_BEST_IN_CLASS.md §A1).
 * Chrome matches docs/specs/BUILDER_DESIGN_SPEC.md Section 12.
 */
import { useState, useRef, useEffect } from "react"
import { ThemeGalleryOverlay } from "./ThemeGalleryOverlay"
import { toast } from "sonner"
import { useCustomizationStore } from "@/stores/customizationStore"
import { supabase } from "@/lib/supabase"
import { uploadVersionedOrgImage } from "@/lib/uploadOrgImage"
import {
  resolveTokens,
  contrastRatio,
  type StoredDesignTokens,
} from "@/lib/designTokens"
import { BUILDER_FONTS, TYPE_PAIRINGS } from "@/lib/typePairings"
import { usePreloadFonts } from "@/hooks/useGoogleFont"
import { TinyInput } from "./TinyInput"
import { FloatingPanel } from "./ui/FloatingPanel"
import { GROUP_LABEL } from "./ui/GroupLabel"

export interface BrandPanelProps {
  primaryColor: string
  onClose: () => void
}

const LABEL_STYLE = GROUP_LABEL

/** Group headers for the Palette / Typography / Feel sections. */
const GROUP_HEADER_STYLE: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: 1.2,
  textTransform: "uppercase" as const,
  color: "rgba(255,255,255,0.55)",
  display: "block",
}

const GROUP_DIVIDER_STYLE: React.CSSProperties = {
  borderTop: "1px solid rgba(255,255,255,0.06)",
  paddingTop: 14,
  display: "flex",
  flexDirection: "column" as const,
  gap: 14,
}

/** Spec Section 12: Brand Panel - left: 16px, top: 100px, width: 272px, z-index: 100 */
const PANEL_POSITION: React.CSSProperties = {
  top: 100,
  left: 16,
  width: 272,
  maxHeight: "calc(100vh - 116px)",
  zIndex: 100,
}

/** Spec Section 12: Preset values (Ocean, Forest, Ember, Amethyst, Sunset, Teal) */
const THEME_PRESETS = [
  { name: "Ocean", primary: "#2563eb", secondary: "#1e40af" },
  { name: "Forest", primary: "#059669", secondary: "#065f46" },
  { name: "Ember", primary: "#dc2626", secondary: "#991b1b" },
  { name: "Amethyst", primary: "#7c3aed", secondary: "#5b21b6" },
  { name: "Sunset", primary: "#ea580c", secondary: "#c2410c" },
  { name: "Teal", primary: "#0891b2", secondary: "#0e7490" },
]

/** Segmented three-way control used by the Feel section. */
function Segmented<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string
  options: readonly { value: T; label: string }[]
  value: T
  onChange: (value: T) => void
}) {
  return (
    <div>
      <label style={LABEL_STYLE}>{label}</label>
      <div style={{ display: "flex", gap: 4 }}>
        {options.map((opt, i) => {
          const active = value === opt.value
          return (
            <button
              key={opt.value}
              type="button"
              aria-pressed={active}
              onClick={() => onChange(opt.value)}
              style={{
                flex: 1,
                padding: "6px 0",
                background: active ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.04)",
                border: active
                  ? "1px solid rgba(255,255,255,0.2)"
                  : "1px solid rgba(255,255,255,0.07)",
                borderRadius:
                  i === 0 ? "7px 0 0 7px" : i === options.length - 1 ? "0 7px 7px 0" : 0,
                color: active ? "#fff" : "rgba(255,255,255,0.45)",
                fontSize: 10,
                fontWeight: 600,
                cursor: "pointer",
                transition: "background 0.12s, border 0.12s, color 0.12s",
              }}
            >
              {opt.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

/** Color picker + hex input row (Palette section). */
function ColorRow({
  label,
  value,
  hexValue,
  placeholder,
  onChange,
}: {
  label: string
  value: string
  hexValue: string
  placeholder: string
  onChange: (val: string) => void
}) {
  return (
    <div>
      <label style={LABEL_STYLE}>{label}</label>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input
          type="color"
          aria-label={`${label} color`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{
            width: 34,
            height: 34,
            border: "none",
            borderRadius: 7,
            cursor: "pointer",
            background: "transparent",
            padding: 0,
          }}
        />
        <TinyInput
          value={hexValue}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          style={{ flex: 1 }}
        />
      </div>
    </div>
  )
}

export function BrandPanel({ primaryColor, onClose }: BrandPanelProps) {
  const { customizations, org, updateColor, updateFont, updateHeadingFont, updateBodyFont, updateFontSize, updateDesignTokens, updateCustomCss, updateLogoScale, updateStickyNav, setOrgLogo } = useCustomizationStore()
  const primary = customizations?.primary_color ?? "#3b82f6"
  const secondary = customizations?.secondary_color ?? "#1e40af"
  const fontFamily = customizations?.font_family ?? "Inter, sans-serif"
  const headingFont = customizations?.heading_font ?? fontFamily
  const bodyFont = customizations?.body_font ?? fontFamily
  const fontSize = (customizations?.font_size ?? "medium") as "small" | "medium" | "large"
  const stickyNav = customizations?.sticky_nav ?? true
  const logoUrl = org?.logo_url ?? null
  const logoScale = customizations?.logo_scale ?? 1.0

  // Resolved token set: stored design_tokens + legacy columns → full derived palette,
  // spacing, shape, elevation. Drives the derived swatches and Feel controls.
  const resolved = resolveTokens(
    (customizations?.design_tokens ?? null) as StoredDesignTokens | null,
    customizations ?? null
  )
  const accent = resolved.colors.accent
  const textOnPrimaryContrast = contrastRatio(resolved.colors.primary, resolved.colors.textOnPrimary)

  const [logoUploading, setLogoUploading] = useState(false)
  const [primaryHex, setPrimaryHex] = useState(primary)
  const [secondaryHex, setSecondaryHex] = useState(secondary)
  const [accentHex, setAccentHex] = useState(accent)
  const logoInputRef = useRef<HTMLInputElement>(null)
  const [cssEditorOpen, setCssEditorOpen] = useState(false)
  const [themeGalleryOpen, setThemeGalleryOpen] = useState(false)
  const [cssLocal, setCssLocal] = useState(customizations?.custom_css ?? "")

  usePreloadFonts(BUILDER_FONTS)

  useEffect(() => {
    setPrimaryHex(primary)
    setSecondaryHex(secondary)
    setAccentHex(accent)
  }, [primary, secondary, accent])

  const applyTokens = (patch: StoredDesignTokens) => {
    updateDesignTokens(patch).catch((err) => {
      console.error("[BrandPanel] token update failed:", err)
      toast.error("Could not save theme change")
    })
  }

  const handleThemeSelect = (theme: { primary: string; secondary: string }) => {
    // Colors only — presets never clobber spacing/shape/elevation token choices.
    void updateColor("primary", theme.primary)
    void updateColor("secondary", theme.secondary)
  }

  const handlePrimaryHexChange = (val: string) => {
    setPrimaryHex(val)
    const hex = val.startsWith("#") ? val : `#${val}`
    if (/^#[0-9A-Fa-f]{6}$/.test(hex)) {
      void updateColor("primary", hex)
    }
  }

  const handleSecondaryHexChange = (val: string) => {
    setSecondaryHex(val)
    const hex = val.startsWith("#") ? val : `#${val}`
    if (/^#[0-9A-Fa-f]{6}$/.test(hex)) {
      void updateColor("secondary", hex)
    }
  }

  const handleAccentHexChange = (val: string) => {
    setAccentHex(val)
    const hex = val.startsWith("#") ? val : `#${val}`
    if (/^#[0-9A-Fa-f]{6}$/.test(hex)) {
      applyTokens({ colors: { accent: hex } })
    }
  }

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !org?.id) return
    const IMAGE_ACCEPT = "image/png,image/jpeg,image/svg+xml"
    const IMAGE_MAX_BYTES = 2 * 1024 * 1024
    if (file.size > IMAGE_MAX_BYTES) {
      toast.error("Logo must be 2MB or smaller")
      return
    }
    const allowed = IMAGE_ACCEPT.split(",").map((m) => m.trim())
    if (!allowed.some((m) => file.type === m)) {
      toast.error("Logo must be PNG, JPEG, or SVG")
      return
    }
    setLogoUploading(true)
    const { data: uploaded, error } = await uploadVersionedOrgImage(
      supabase,
      "logos",
      `${org.id}/logo`,
      file
    )
    if (error || !uploaded) {
      setLogoUploading(false)
      toast.error(error?.message ?? "Upload failed")
      return
    }
    await supabase.from("organizations").update({ logo_url: uploaded.publicUrl }).eq("id", org.id)
    setOrgLogo(uploaded.publicUrl)
    setLogoUploading(false)
    toast.success("Logo uploaded")
    e.target.value = ""
  }

  const handleRemoveLogo = async () => {
    if (!org?.id) return
    await supabase.from("organizations").update({ logo_url: null }).eq("id", org.id)
    setOrgLogo(null)
    toast.success("Logo removed")
  }

  const activeThemeIndex = THEME_PRESETS.findIndex(
    (t) =>
      t.primary.toLowerCase() === primary.toLowerCase() &&
      t.secondary.toLowerCase() === secondary.toLowerCase()
  )

  const derivedSwatches = [
    { name: "Tint", color: resolved.colors.primaryTint },
    { name: "Shade", color: resolved.colors.primaryShade },
    { name: "Text", color: resolved.colors.textOnPrimary },
  ]

  return (
    <>
    <ThemeGalleryOverlay open={themeGalleryOpen} onClose={() => setThemeGalleryOpen(false)} />
    <FloatingPanel
      className="animate-slideIn"
      position={PANEL_POSITION}
      title="Brand"
      dotColor={primaryColor}
      onClose={onClose}
    >
      <style>{`
        .nos-brand-slider {
          -webkit-appearance: none;
          appearance: none;
          width: 100%;
          height: 4px;
          border-radius: 2px;
          background: rgba(255,255,255,0.1);
          outline: none;
          cursor: pointer;
        }
        .nos-brand-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: #fff;
          box-shadow: 0 1px 4px rgba(0,0,0,0.5);
          cursor: pointer;
          transition: transform 0.1s;
          margin-top: -5px;
        }
        .nos-brand-slider::-moz-range-thumb {
          width: 14px;
          height: 14px;
          border-radius: 50%;
          border: none;
          background: #fff;
          box-shadow: 0 1px 4px rgba(0,0,0,0.5);
          cursor: pointer;
        }
        .nos-brand-slider::-webkit-slider-runnable-track {
          height: 4px;
          border-radius: 2px;
        }
        .nos-brand-slider:active::-webkit-slider-thumb {
          transform: scale(1.2);
        }
      `}</style>
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: 16,
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        {/* THEME - 6 color swatches */}
        <div>
          <label style={LABEL_STYLE}>Theme</label>
          <div
            style={{
              display: "flex",
              gap: 6,
              flexWrap: "wrap",
            }}
          >
            {THEME_PRESETS.map((theme, i) => (
              <button
                key={i}
                type="button"
                onClick={() => handleThemeSelect(theme)}
                title={theme.name}
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 9,
                  background: `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})`,
                  border:
                    activeThemeIndex === i
                      ? "2px solid #fff"
                      : "2px solid transparent",
                  cursor: "pointer",
                  boxSizing: "border-box",
                  transition: "border 0.12s",
                }}
              />
            ))}
          </div>
          {/* NOS-158: full theme gallery (tokens + layouts, preview before apply) */}
          <button
            type="button"
            data-testid="browse-themes-btn"
            onClick={() => setThemeGalleryOpen(true)}
            style={{
              marginTop: 10,
              width: "100%",
              padding: "8px 0",
              borderRadius: 8,
              border: "1px solid rgba(255,255,255,0.1)",
              background: "rgba(255,255,255,0.04)",
              color: "rgba(255,255,255,0.75)",
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Browse all themes
          </button>
        </div>

        {/* ============ PALETTE ============ */}
        <div style={GROUP_DIVIDER_STYLE}>
          <span style={GROUP_HEADER_STYLE}>Palette</span>

          <ColorRow
            label="Primary"
            value={primary}
            hexValue={primaryHex}
            placeholder="#2563eb"
            onChange={handlePrimaryHexChange}
          />

          <ColorRow
            label="Secondary"
            value={secondary}
            hexValue={secondaryHex}
            placeholder="#1e40af"
            onChange={handleSecondaryHexChange}
          />

          <div>
            <ColorRow
              label="Accent"
              value={accent}
              hexValue={accentHex}
              placeholder="#0ea5e9"
              onChange={handleAccentHexChange}
            />
            <p
              style={{
                marginTop: 4,
                marginBottom: 0,
                fontSize: 9.5,
                color: "rgba(255,255,255,0.3)",
                lineHeight: 1.4,
              }}
            >
              Colors gradients and stat highlights. Matches Secondary until you change it.
            </p>
          </div>

          {/* Derived swatches — computed by resolveTokens, read-only */}
          <div>
            <label style={LABEL_STYLE}>Derived 🔒</label>
            <div style={{ display: "flex", gap: 6 }} aria-label="Derived colors (read-only)">
              {derivedSwatches.map((swatch) => (
                <div
                  key={swatch.name}
                  title={`${swatch.name}: ${swatch.color} — computed automatically from Primary`}
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 4,
                    cursor: "not-allowed",
                  }}
                >
                  <div
                    data-testid={`derived-${swatch.name.toLowerCase()}`}
                    style={{
                      width: "100%",
                      height: 22,
                      borderRadius: 6,
                      background: swatch.color,
                      border: "1px solid rgba(255,255,255,0.12)",
                      boxSizing: "border-box",
                      opacity: 0.85,
                    }}
                  />
                  <span
                    style={{
                      fontSize: 9,
                      color: "rgba(255,255,255,0.35)",
                    }}
                  >
                    {swatch.name}
                  </span>
                </div>
              ))}
            </div>
            <p
              style={{
                marginTop: 4,
                marginBottom: 0,
                fontSize: 9.5,
                color: "rgba(255,255,255,0.3)",
                lineHeight: 1.4,
              }}
            >
              Computed from Primary — updates automatically, not editable.
            </p>
          </div>

          {/* WCAG AA contrast warning — non-blocking */}
          {textOnPrimaryContrast < 3 && (
            <div
              role="alert"
              style={{
                background: "rgba(245,158,11,0.12)",
                border: "1px solid rgba(245,158,11,0.35)",
                borderRadius: 8,
                padding: "8px 10px",
                color: "#fbbf24",
                fontSize: 10.5,
                lineHeight: 1.5,
              }}
            >
              Low contrast: text on your primary color falls below WCAG AA (3:1). Buttons and hero text may be hard to read — try a darker or lighter primary.
            </div>
          )}
        </div>

        {/* ============ TYPOGRAPHY ============ */}
        <div style={GROUP_DIVIDER_STYLE}>
          <span style={GROUP_HEADER_STYLE}>Typography</span>

          {/* Pairing presets - heading/body combos */}
          <div>
            <label style={LABEL_STYLE}>Pairings</label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
              {TYPE_PAIRINGS.map((pairing) => {
                const active = headingFont === pairing.heading && bodyFont === pairing.body
                return (
                  <button
                    key={pairing.name}
                    type="button"
                    title={pairing.name}
                    aria-pressed={active}
                    onClick={() =>
                      applyTokens({
                        typography: {
                          headingFamily: pairing.heading,
                          bodyFamily: pairing.body,
                        },
                      })
                    }
                    style={{
                      padding: "8px 2px 6px",
                      background: active ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.04)",
                      border: active
                        ? "1px solid rgba(255,255,255,0.25)"
                        : "1px solid rgba(255,255,255,0.07)",
                      borderRadius: 8,
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 2,
                      transition: "background 0.12s, border 0.12s",
                    }}
                  >
                    <span
                      style={{
                        fontFamily: pairing.heading,
                        fontSize: 14,
                        lineHeight: 1,
                        color: active ? "#fff" : "rgba(255,255,255,0.7)",
                      }}
                    >
                      Ag
                    </span>
                    <span
                      style={{
                        fontSize: 8.5,
                        color: active ? "rgba(255,255,255,0.7)" : "rgba(255,255,255,0.4)",
                      }}
                    >
                      {pairing.name}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>

          {/* HEADING FONT - dropdown */}
          <div>
            <label style={LABEL_STYLE}>Heading Font</label>
            <select
              value={headingFont}
              onChange={(e) => void updateHeadingFont(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 10px",
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 8,
                color: "#fff",
                fontSize: 12,
                fontFamily: headingFont,
                cursor: "pointer",
                appearance: "none",
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='rgba(255,255,255,0.5)' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 10px center",
                paddingRight: 28,
              }}
            >
              {BUILDER_FONTS.map((font) => (
                <option key={font.value} value={font.value} style={{ background: "#1a1a1e", color: "#fff" }}>
                  {font.name}
                </option>
              ))}
            </select>
          </div>

          {/* BODY FONT - dropdown */}
          <div>
            <label style={LABEL_STYLE}>Body Font</label>
            <select
              value={bodyFont}
              onChange={(e) => {
                void updateBodyFont(e.target.value)
                void updateFont(e.target.value)
              }}
              style={{
                width: "100%",
                padding: "8px 10px",
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 8,
                color: "#fff",
                fontSize: 12,
                fontFamily: bodyFont,
                cursor: "pointer",
                appearance: "none",
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='rgba(255,255,255,0.5)' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 10px center",
                paddingRight: 28,
              }}
            >
              {BUILDER_FONTS.map((font) => (
                <option key={font.value} value={font.value} style={{ background: "#1a1a1e", color: "#fff" }}>
                  {font.name}
                </option>
              ))}
            </select>
          </div>

          {/* FONT SIZE - S / M / L segmented control */}
          <div>
            <label style={LABEL_STYLE}>Font Size</label>
            <div style={{ display: "flex", gap: 4 }}>
              {(["small", "medium", "large"] as const).map((size, i) => (
                <button
                  key={size}
                  type="button"
                  onClick={() => void updateFontSize(size)}
                  style={{
                    flex: 1,
                    padding: "6px 0",
                    background: fontSize === size ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.04)",
                    border: fontSize === size ? "1px solid rgba(255,255,255,0.2)" : "1px solid rgba(255,255,255,0.07)",
                    borderRadius: i === 0 ? "7px 0 0 7px" : i === 2 ? "0 7px 7px 0" : 0,
                    color: fontSize === size ? "#fff" : "rgba(255,255,255,0.45)",
                    fontSize: i === 0 ? 10 : i === 1 ? 12 : 14,
                    fontWeight: 600,
                    cursor: "pointer",
                    transition: "background 0.12s, border 0.12s, color 0.12s",
                  }}
                >
                  {size === "small" ? "S" : size === "medium" ? "M" : "L"}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ============ FEEL ============ */}
        <div style={GROUP_DIVIDER_STYLE}>
          <span style={GROUP_HEADER_STYLE}>Feel</span>

          <Segmented
            label="Section spacing"
            value={resolved.spacing.sectionY}
            options={[
              { value: "compact", label: "Compact" },
              { value: "regular", label: "Regular" },
              { value: "airy", label: "Airy" },
            ] as const}
            onChange={(sectionY) => applyTokens({ spacing: { sectionY } })}
          />

          <Segmented
            label="Corners"
            value={resolved.shape.radius}
            options={[
              { value: "sharp", label: "Sharp" },
              { value: "soft", label: "Soft" },
              { value: "round", label: "Round" },
            ] as const}
            onChange={(radius) => applyTokens({ shape: { radius } })}
          />

          <Segmented
            label="Buttons"
            value={resolved.shape.buttonStyle}
            options={[
              { value: "solid", label: "Solid" },
              { value: "outline", label: "Outline" },
              { value: "pill", label: "Pill" },
            ] as const}
            onChange={(buttonStyle) => applyTokens({ shape: { buttonStyle } })}
          />

          <Segmented
            label="Elevation"
            value={resolved.elevation}
            options={[
              { value: "flat", label: "Flat" },
              { value: "soft", label: "Soft" },
              { value: "layered", label: "Layered" },
            ] as const}
            onChange={(elevation) => applyTokens({ elevation })}
          />
        </div>

        {/* LOGO - upload zone */}
        <div style={GROUP_DIVIDER_STYLE}>
        <div>
          <label style={LABEL_STYLE}>Logo</label>
          <input
            ref={logoInputRef}
            type="file"
            accept="image/png,image/jpeg,image/svg+xml"
            onChange={handleLogoUpload}
            style={{ display: "none" }}
            disabled={logoUploading}
          />
          {logoUrl ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{
                height: 80,
                borderRadius: 8,
                border: "1px solid rgba(255,255,255,0.08)",
                background: "rgba(255,255,255,0.03)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                padding: "8px 12px",
              }}>
                <img
                  src={logoUrl}
                  alt="Logo"
                  style={{ maxHeight: 64, maxWidth: "100%", objectFit: "contain" }}
                />
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <button
                  type="button"
                  onClick={() => logoInputRef.current?.click()}
                  disabled={logoUploading}
                  style={{
                    flex: 1,
                    padding: "5px 0",
                    background: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 6,
                    color: "rgba(255,255,255,0.7)",
                    fontSize: 11,
                    cursor: "pointer",
                  }}
                >
                  {logoUploading ? "Uploading…" : "Replace"}
                </button>
                <button
                  type="button"
                  onClick={() => void handleRemoveLogo()}
                  style={{
                    flex: 1,
                    padding: "5px 0",
                    background: "rgba(239,68,68,0.12)",
                    border: "1px solid rgba(239,68,68,0.25)",
                    borderRadius: 6,
                    color: "#ef4444",
                    fontSize: 11,
                    cursor: "pointer",
                  }}
                >
                  Remove
                </button>
              </div>
            </div>
          ) : (
            <div
              role="button"
              tabIndex={0}
              onClick={() => logoInputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") logoInputRef.current?.click()
              }}
              style={{
                height: 56,
                borderRadius: 8,
                border: "1px dashed rgba(255,255,255,0.1)",
                background: "rgba(255,255,255,0.02)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "rgba(255,255,255,0.25)",
                fontSize: 11,
                cursor: "pointer",
              }}
            >
              {logoUploading ? <span>Uploading…</span> : <span>＋ Upload logo</span>}
            </div>
          )}
          <p style={{
            marginTop: 6,
            fontSize: 10,
            color: "rgba(255,255,255,0.25)",
            lineHeight: 1.5,
          }}>
            PNG or SVG &middot; transparent background &middot; upload at least 400px wide for crispness
          </p>
        </div>

        {/* LOGO SCALE */}
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <label style={{ ...LABEL_STYLE, marginBottom: 0, display: "block" }}>Logo size</label>
            <span style={{ fontSize: 10, color: "rgba(255,255,255,0.4)" }}>
              {Math.round(logoScale * 100)}%
            </span>
          </div>
          {logoUrl && (
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "16px 12px",
              marginBottom: 8,
              borderRadius: 6,
              background: "rgba(255,255,255,0.02)",
              border: "1px solid rgba(255,255,255,0.06)",
            }}>
              <img
                src={logoUrl}
                alt=""
                style={{
                  height: Math.round(64 * logoScale),
                  width: "auto",
                  maxWidth: "100%",
                  display: "block",
                  transition: "height 0.1s",
                }}
              />
            </div>
          )}
          <input
            type="range"
            min={0.5}
            max={2.0}
            step={0.05}
            value={logoScale}
            onChange={(e) => void updateLogoScale(parseFloat(e.target.value))}
            className="nos-brand-slider"
            style={{
              background: `linear-gradient(to right, ${primaryColor} 0%, ${primaryColor} ${((logoScale - 0.5) / 1.5) * 100}%, rgba(255,255,255,0.1) ${((logoScale - 0.5) / 1.5) * 100}%, rgba(255,255,255,0.1) 100%)`,
            }}
          />
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
            <span style={{ fontSize: 9, color: "rgba(255,255,255,0.25)" }}>50%</span>
            <span style={{ fontSize: 9, color: "rgba(255,255,255,0.25)" }}>200%</span>
          </div>
        </div>

        {/* STICKY NAV */}
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <label style={{ ...LABEL_STYLE, marginBottom: 0 }}>Sticky nav</label>
            <button
              type="button"
              role="switch"
              aria-checked={stickyNav}
              onClick={() => void updateStickyNav(!stickyNav)}
              style={{
                width: 36,
                height: 20,
                borderRadius: 10,
                background: stickyNav ? primaryColor : "rgba(255,255,255,0.08)",
                border: stickyNav ? "none" : "1px solid rgba(255,255,255,0.15)",
                cursor: "pointer",
                position: "relative",
                transition: "background 0.15s, border 0.15s",
                flexShrink: 0,
                boxSizing: "border-box",
              }}
            >
              <span
                style={{
                  position: "absolute",
                  top: stickyNav ? 3 : 2,
                  left: stickyNav ? 19 : 2,
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  background: stickyNav ? "#fff" : "rgba(255,255,255,0.5)",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.4)",
                  transition: "left 0.15s, background 0.15s, top 0.15s",
                  display: "block",
                }}
              />
            </button>
          </div>
          {stickyNav && (
            <p style={{
              marginTop: 6,
              fontSize: 10,
              color: "rgba(255,255,255,0.3)",
              lineHeight: 1.4,
            }}>
              ⓘ Not visible in canvas preview
            </p>
          )}
        </div>
        </div>

        {/* Custom CSS */}
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 14 }}>
          <button
            type="button"
            onClick={() => setCssEditorOpen(!cssEditorOpen)}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              background: "none",
              border: "none",
              padding: "2px 0",
              cursor: "pointer",
            }}
          >
            <span style={LABEL_STYLE}>Custom CSS</span>
            <span style={{ fontSize: 11, color: "rgba(255,255,255,0.3)" }}>
              {cssEditorOpen ? "−" : "+"}
            </span>
          </button>
          {cssEditorOpen && (
            <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 8 }}>
              <p style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", margin: 0, lineHeight: 1.5 }}>
                Applied globally to every page. Use custom class names here and apply them to sections or elements.
              </p>
              <textarea
                value={cssLocal}
                onChange={(e) => setCssLocal(e.target.value)}
                placeholder={".my-section {\n  background: #f0f4ff;\n  padding: 40px;\n}"}
                rows={10}
                spellCheck={false}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  background: "rgba(0,0,0,0.4)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: 8,
                  color: "#a8d8a8",
                  fontSize: 11,
                  fontFamily: "'Fira Code', 'Cascadia Code', 'Courier New', monospace",
                  lineHeight: 1.6,
                  resize: "vertical" as const,
                  outline: "none",
                  boxSizing: "border-box" as const,
                }}
                onBlur={() => { void updateCustomCss(cssLocal) }}
              />
            </div>
          )}
        </div>
      </div>
    </FloatingPanel>
    </>
  )
}
